import { useQuery } from "@tanstack/react-query";
import type { UserListPayload } from "@dealer-center/ddc-pages-configuration-interface";
import {
  CONFIGURATION_QUERY_KEY_BASE,
  configurationRequest
} from "./configurationRequest";

/** FR-014's cap. At most 50 rows reach the UI per page. */
export const USER_LIST_PAGE_SIZE = 50;

/**
 * FR-013's four sort orders, as an `as const` union — **never an `enum`** (Principle III).
 *
 * The key and the direction are separate because the wire takes them separately, and because
 * "Name (A–Z)" and "Name (Z–A)" are one key with two directions rather than two keys.
 */
export const USER_LIST_SORT_KEYS = ["name", "username", "email"] as const;
export type UserListSortKey = (typeof USER_LIST_SORT_KEYS)[number];

export const USER_LIST_SORT_DIRS = ["asc", "desc"] as const;
export type UserListSortDir = (typeof USER_LIST_SORT_DIRS)[number];

export interface UseGetUserListOptions {
  accountId?: string;
  /**
   * The filter term, **already debounced by the caller** (`useUserListQuery`).
   *
   * It is debounced *before* it reaches this hook rather than inside it, because the term is part
   * of the query key: keying off every keystroke would put one React Query cache entry per
   * character alongside one request per character (FR-012, FR-012a).
   */
  filter?: string;
  sortKey?: UserListSortKey;
  sortDir?: UserListSortDir;
  /** Zero-based, matching `useGetLoginHistory` and the wire. */
  page?: number;
  size?: number;
}

/**
 * Reads one page of the account's users (FR-010, FR-012a, FR-014).
 *
 * **Account-scoped, not user-scoped.** Every other hook in this lib reads a single user's record
 * and passes `userId` as a path segment; this one's subject is the **account**, so it passes **no
 * `userId`** and the URL is `/configuration/userlist?accountId=…`. That shape only became
 * expressible once `ConfigurationRequestOptions.userId` was made optional — see T026a and
 * `buildConfigurationUrl`. **Do not** pass an empty or dummy `userId` to avoid that: an empty
 * string yields a trailing `/` the BFF dispatches as a `resourceId`.
 *
 * **Server-paginated, server-filtered and server-sorted** (FR-012a). Like `useGetLoginHistory`,
 * the BFF returns exactly one page and does not aggregate, so the response's `totalCount` /
 * `totalPages` are the only source of truth for the size of the result set — `users.length`
 * describes the current page and nothing more. That is what FR-011's count line reads.
 *
 * `configurationRequest` is imported **relatively** and stays unexported from the lib barrel. The
 * barrel explains why: publishing it would re-create the generic resource-string pattern Principle
 * V forbids at the call site, and put an API call in `react/` against Principle I.
 *
 * **The BFF route exists as of 2026-09-14** (`get-user-list.ts`). Its **rows** pass through, but its
 * query and envelope do not — three differences between CC9's contract and this hook's wire shape
 * live entirely on that side, so nothing here needs to know about them. Do not "align" this hook to
 * CC9's parameter names on the assumption that a BFF resource is a pass-through end to end:
 *
 * | This hook sends | CC9 takes | Who translates |
 * |---|---|---|
 * | `sortKey` ∈ `name`/`username`/`email` | `sort` ∈ `firstName`/`userId`/`email` | the BFF |
 * | — | no `totalPages` in the response | the BFF derives it |
 * | — | **404** when the account has no users | the BFF returns the empty payload |
 *
 * The row is CC9's own, forwarded whole as of 2026-09-17 (reviewer decision) — `picture` and
 * `lastLogin` included, plus a `lastSignIn` derived from `lastLogin` beside it. Read `lastSignIn`;
 * the duplicate spelling exists so the forwarding stays a spread.
 *
 * `page`, `size`, `sortDir` and `filter` already match CC9's own names and semantics and are
 * forwarded unchanged. `ddc.controlcenter.api.user.read` was already granted on the `configuration`
 * function's `SCOPES`, so the route needed no new authorization (D-15, closed for the read).
 */
export const useGetUserList = ({
  accountId,
  filter,
  sortKey = "name",
  sortDir = "asc",
  page = 0,
  size = USER_LIST_PAGE_SIZE
}: UseGetUserListOptions) =>
  useQuery({
    /*
     * Every request parameter belongs in the key. Without `page`/`size`, paging forward would
     * serve the previous page's rows from cache; without `filter`, narrowing the search would show
     * the unfiltered rows; without the sort pair, reordering would be a no-op on screen.
     */
    queryKey: [
      CONFIGURATION_QUERY_KEY_BASE,
      "userlist",
      accountId,
      filter,
      sortKey,
      sortDir,
      page,
      size
    ],
    queryFn: () => {
      if (!accountId) {
        throw new Error("useGetUserList requires an accountId");
      }

      return configurationRequest<UserListPayload>({
        resource: "userlist",
        // No `userId` — the subject is the account. See the note above.
        accountId,
        /*
         * `configurationRequest` omits only `undefined`, so a numeric 0 survives — page 0 is a
         * real page and dropping it would silently request the BFF's default instead. An empty
         * filter term is normalised to `undefined` so an unfiltered read sends no `filter` param
         * at all rather than `filter=`.
         */
        params: {
          filter: filter?.trim() ? filter.trim() : undefined,
          sortKey,
          sortDir,
          page,
          size
        }
      });
    },
    enabled: Boolean(accountId),
    staleTime: 1000 * 60 * 5
  });
